/**
 * Calculator history — a portal-dashboard section that renders the user's
 * saved calculator snapshots (`/api/portal/calculator-saves`) as a chart
 * plus a compact table, grouped by calculator type (Testzsír / Adonis /
 * Kaliper).
 *
 * Rendered on `/fiokom` below the training progress section, matches the
 * TrainingSection visual language (light card, dark section header pill).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';
import { Loader2, Bookmark, ChevronDown, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { portalCalculatorSavesList, portalCalculatorSaveDelete } from '../services/api';

const TYPE_META = {
  testzsir: {
    label:  'Testzsír + Kalória',
    color:  '#eab308',
    metricLabel: 'Testzsír %',
    getY:   (row) => row?.output?.body_fat_pct,
    unit:   '%',
    columns: [
      ['Testzsír %',   (r) => r?.output?.body_fat_pct  != null ? `${r.output.body_fat_pct}%`  : '—'],
      ['Cél kalória',   (r) => r?.output?.target_kcal   != null ? `${r.output.target_kcal.toLocaleString('hu-HU')} kcal` : '—'],
      ['Sovány tömeg',  (r) => r?.output?.lean_mass_kg  != null ? `${r.output.lean_mass_kg} kg` : '—'],
    ],
  },
  adonis: {
    label:  'Adonis / Venus Index',
    color:  '#a855f7',
    metricLabel: 'Pontszám',
    getY:   (row) => row?.output?.score,
    unit:   'pont',
    columns: [
      ['Pontszám', (r) => r?.output?.score != null ? r.output.score : '—'],
      ['Arány',     (r) => r?.output?.ratio != null ? r.output.ratio : '—'],
      ['Ideális',   (r) => r?.output?.ideal_ratio != null ? r.output.ideal_ratio : '—'],
    ],
  },
  kaliper: {
    label:  'Kaliper (bőrredő)',
    color:  '#10b981',
    metricLabel: 'Testzsír %',
    getY:   (row) => row?.output?.body_fat_pct,
    unit:   '%',
    columns: [
      ['Testzsír %', (r) => r?.output?.body_fat_pct != null ? `${r.output.body_fat_pct}%` : '—'],
      ['Σ redő',    (r) => r?.output?.sum_mm != null ? `${r.output.sum_mm} mm` : '—'],
      ['Sovány tömeg', (r) => r?.output?.lean_mass_kg != null ? `${r.output.lean_mass_kg} kg` : '—'],
    ],
  },
};

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(d.getDate()).padStart(2, '0')}.`;
  } catch { return iso; }
};

const CalculatorHistorySection = () => {
  const [saves, setSaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openType, setOpenType] = useState('testzsir'); // first tab open by default

  const load = async () => {
    setLoading(true);
    try {
      const d = await portalCalculatorSavesList();
      setSaves(d.saves || []);
    } catch (err) {
      if (err?.response?.status !== 401) { /* silent on auth-missing */ }
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const savesByType = useMemo(() => {
    const g = { testzsir: [], adonis: [], kaliper: [] };
    (saves || []).forEach((s) => { if (g[s.type]) g[s.type].push(s); });
    Object.values(g).forEach((list) => list.sort((a, b) => new Date(a.created_at) - new Date(b.created_at)));
    return g;
  }, [saves]);

  const totalCount = saves.length;
  if (loading) {
    return (
      <section data-testid="calc-history-section" className="bg-white text-gray-900 rounded-2xl shadow-sm border border-gray-100 p-6 mt-6">
        <div className="flex items-center gap-2 text-gray-500 text-sm"><Loader2 className="animate-spin" size={14} /> Betöltés…</div>
      </section>
    );
  }
  if (totalCount === 0) {
    return (
      <section data-testid="calc-history-section" className="bg-white text-gray-900 rounded-2xl shadow-sm border border-gray-100 p-6 mt-6">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500 mb-3 font-bold">
          <Bookmark size={14} className="text-yellow-600" /> Kalkulátor napló
        </div>
        <p className="text-sm text-gray-600">
          Még nincs elmentett kalkulátor-eredményed. A főoldali <a className="text-yellow-700 hover:text-yellow-800 underline underline-offset-2" href="/#calculator">Kalkulátorok</a> szekcióban minden számítás mellett megjelenik a „Mentés fiókba" gomb — kattints rá, és az eredmények itt jelennek meg időrendben.
        </p>
      </section>
    );
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Biztosan törlöd ezt a mentést?')) return;
    try {
      await portalCalculatorSaveDelete(id);
      setSaves((cur) => cur.filter((s) => s.id !== id));
      toast.success('Mentés törölve.');
    } catch { toast.error('Törlés sikertelen.'); }
  };

  return (
    <section
      data-testid="calc-history-section"
      className="bg-white text-gray-900 rounded-2xl shadow-sm border border-gray-100 p-6 mt-6"
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500 mb-4 font-bold">
        <Bookmark size={14} className="text-yellow-600" /> Kalkulátor napló
        <span className="text-gray-400 normal-case tracking-normal font-normal">· {totalCount} mentés</span>
      </div>

      {/* Type tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {Object.entries(TYPE_META).map(([key, meta]) => {
          const count = savesByType[key]?.length || 0;
          const active = openType === key;
          return (
            <button
              key={key}
              type="button"
              data-testid={`calc-history-tab-${key}`}
              onClick={() => setOpenType(key)}
              disabled={count === 0}
              className={`px-3 h-9 rounded-md text-sm font-semibold transition-all border ${
                active
                  ? 'bg-gray-900 text-white border-gray-900'
                  : count === 0
                    ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
              }`}
            >
              {meta.label} <span className={`ml-1 ${active ? 'text-gray-300' : 'text-gray-400'}`}>· {count}</span>
            </button>
          );
        })}
      </div>

      {(() => {
        const meta = TYPE_META[openType];
        const rows = savesByType[openType] || [];
        if (rows.length === 0) {
          return (
            <p className="text-sm text-gray-500 py-6 text-center">
              Nincs még {meta.label.toLowerCase()} mentésed. Nyisd meg a főoldal <a href={`/#calculator`} className="text-yellow-700 underline">Kalkulátorok</a> szekcióját.
            </p>
          );
        }
        // Chart data: date + Y-metric per calculator type
        const chartData = rows.map((r) => ({
          date:  fmtDate(r.created_at),
          value: meta.getY(r),
        })).filter((r) => r.value != null);

        return (
          <div>
            {chartData.length >= 2 && (
              <div className="h-56 mb-6" data-testid={`calc-history-chart-${openType}`}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 5, right: 15, bottom: 0, left: -10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#6b7280' }} padding={{ left: 18, right: 18 }} />
                    <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} domain={['dataMin - 1', 'dataMax + 1']} />
                    <Tooltip formatter={(v) => [`${v}${meta.unit ? ' ' + meta.unit : ''}`, meta.metricLabel]} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Line type="monotone" name={meta.metricLabel} dataKey="value" stroke={meta.color} strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
            {chartData.length === 1 && (
              <p className="text-xs text-gray-500 italic mb-4">
                Egy mentés van — legalább kettő kell a grafikonhoz. Menj vissza a kalkulátorhoz és rögzíts egy újabb mérést.
              </p>
            )}

            {/* Table */}
            <div className="overflow-x-auto rounded-lg border border-gray-100">
              <table className="w-full text-sm">
                <thead className="text-[10px] text-gray-500 uppercase tracking-wider bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-left px-3 py-2">Dátum</th>
                    {meta.columns.map(([label]) => <th key={label} className="text-right px-3 py-2">{label}</th>)}
                    <th className="text-right px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {[...rows].reverse().map((r) => (
                    <tr key={r.id} className="border-b border-gray-100 last:border-b-0" data-testid={`calc-history-row-${r.id}`}>
                      <td className="px-3 py-2 text-gray-700 font-medium whitespace-nowrap">{fmtDate(r.created_at)}</td>
                      {meta.columns.map(([label, val]) => (
                        <td key={label} className="text-right px-3 py-2 text-gray-800 font-mono">{val(r)}</td>
                      ))}
                      <td className="text-right px-2 py-2">
                        <button
                          type="button"
                          onClick={() => handleDelete(r.id)}
                          data-testid={`calc-history-delete-${r.id}`}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                          aria-label="Mentés törlése"
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </section>
  );
};

export default CalculatorHistorySection;
